import { useState } from "react";

/**
 * React Hook to provide local functioniality to handle api calls
 *
 * @param {Object} options
 *    apiFn: Function*
 *    defaultValue: Any
 *    initialRequest: Object
 *    transformRequest: Function -> Object
 *    transformResponse: Function -> Any
 *    transformError: Function -> String
 *    onError: Function
 *    onResponse: Function
 * @return {object}
 *    response : Any
 *    setRequestParams : Function
 *    error  : Error
 *    fetchData  : Function
 *    isFetching  : Boolean
 * @example
    const { response, setRequestParams, error, fetchData, isFetching } = useFetch(put, "patients", {
      defaultValue: [],
      initialRequest: { page: 1, perPage: 20 },
      transformResponse: response => response.patients
    });
 */
const useFetch = ({
  apiFn,
  defaultValue,
  transformResponse,
  transformError,
  onSuccess,
  onError
}) => {
  const [response, setResponse] = useState(defaultValue);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState();

  const fetchData = async params => {
    try {
      setIsFetching(true);
      setResponse(defaultValue);
      setError();
      const response = await apiFn(params);
      const result =
        typeof transformResponse === "function"
          ? transformResponse(response)
          : response;
      setResponse(result);
      if (typeof onSuccess === "function") onSuccess(result);
      return result;
    } catch (error) {
      if (typeof transformError === "function") {
        setError(transformError(error));
      } else setError(error);
      if (typeof onError === "function") onError(error);
    } finally {
      setIsFetching(false);
    }
  };

  return {
    response,
    error,
    fetchData,
    isFetching
  };
};

export default useFetch;
